import { Form, notify } from '@/components';
import {
  PersonalizationItemState,
  useSavePersonalizationItemManualPriceMutation,
} from '@/redux';
import { Button, Modal } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  PersonalizationItemPricingDataDto,
  PricingType,
  SalesTaxType,
} from '@/core';

interface Props {
  item: PersonalizationItemState;
  onClose: () => any;
  type: PricingType;
  salesTaxType?: SalesTaxType;
}

const SCHEMA = Yup.object().shape({
  amount: Yup.number().min(0).nullable().required(),
});

type FormValue = OmitIndex<Yup.InferType<typeof SCHEMA>>;

function useSubmit(props: Props) {
  const { item, onClose, type } = props;
  const [save] = useSavePersonalizationItemManualPriceMutation();
  return useCallback(
    ({ amount }: FormValue) =>
      save({
        id: item.id,
        amount: amount,
        type,
      })
        .unwrap()
        .then(() => {
          notify.success.t('personalization.summary.manualPrice.saved');
          onClose();
        }),
    [save, item.id, type, onClose],
  );
}

export function getManualPriceByType(
  type: PricingType,
  pricing: PersonalizationItemPricingDataDto,
  salesTaxType: SalesTaxType,
): number | undefined {
  const {
    totalBaseCost,
    totalOwnerPrice,
    totalResidentPrice,
    residentPriceWithoutTax,
  } = pricing;

  const valueByType: Record<PricingType, number | undefined> = {
    Resident:
      salesTaxType === SalesTaxType.ItemLevel
        ? totalResidentPrice
        : residentPriceWithoutTax,
    Owner: totalOwnerPrice,
    Base: totalBaseCost,
  };

  return valueByType[type];
}

function useInitialValue(props: Props): FormValue {
  const { type, salesTaxType, item } = props;

  return useMemo(
    () => ({
      amount: item.pricing
        ? getManualPriceByType(
            type,
            item.pricing,
            salesTaxType ?? 'SummaryLevel',
          ) ?? null!
        : null!,
    }),
    [type, item.pricing, salesTaxType],
  );
}

export function ManualPriceModal(props: Props) {
  const { onClose } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'personalization.summary.manualPrice',
  });

  const handleSubmit = useSubmit(props);
  const initialValue = useInitialValue(props);

  return (
    <Modal
      visible
      onCancel={onClose}
      title={t('title')}
      footer={[
        <Button key="back" type="default" onClick={onClose}>
          {t('cancel')}
        </Button>,
        <Form.Submit uid="manual-price" key="submit" type="primary">
          {t('save')}
        </Form.Submit>,
      ]}
    >
      <Form.Formik<FormValue>
        uid="manual-price"
        i18n="personalization.summary.manualPrice"
        validationSchema={SCHEMA}
        onSubmit={handleSubmit}
        initialValues={initialValue}
      >
        <Form.Number name="amount" required />
      </Form.Formik>
    </Modal>
  );
}
